// FRIDGE-156 — the complete image-host allowlist for /_next/image.
//
// Derived 07/26/2026 from every distinct host across the 15 image-bearing
// columns in 15 tables (products.images, products.images_supabase,
// canonical_products.images, articles.image, authors.avatar_url,
// brands.logo_path, collections.featured_image, cpsc_recalls.image_url,
// fin_videos.thumbnail_url, hub_pages.featured_image, press.logo_path,
// press_releases.image, recycling_providers.logo_url,
// state_energy_rates.flag_image_url, utilities.logo_url). The reproduce query
// is recorded on the FRIDGE-156 card; re-derive against production before
// removing entries.
//
// Rollout state: **Step 2 landed 08/16/2026 — the open '**' is GONE.** From
// here a host that is not on this list gets a 400 from /_next/image, so add
// the host here rather than reaching for a wildcard. The guard test in
// tests/image-host-allowlist.test.ts holds WILDCARD_ALLOWED at false.
//
// Re-checked live 08/16/2026 against the same 15 columns before the wildcard
// came out, because the 07/26 census was three weeks old and the list is only
// as safe as its last check. Three corrections came back:
//   + img.einpresswire.com — arrived with the press-release wave, 13 uses.
//     Shipping the 07/26 list unchanged would have 400'd every one of them.
//   - images-na.ssl-images-amazon.com, target.scene7.com — zero uses in any
//     of the 15 columns and zero in articles.content; these are the "dead
//     specific entries" the card's Done-when #4 asks to remove.
//
// Plain CommonJS because next.config.js requires it at build time.

// The Supabase storage host is the project host; derive it from env rather
// than hardcoding (the pre-commit scanner rejects the literal, and env is the
// single source of truth for which project serves storage).
const supabaseHost = (() => {
	try {
		return new URL(process.env.NEXT_PUBLIC_SUPABASE_URL || '').hostname;
	} catch {
		return null;
	}
})();

const IMAGE_HOSTS = [
	// Top volume (uses as of 07/26/2026)
	'm.media-amazon.com', // 95,760
	'assets.wfcdn.com', // 6,747
	'assets.ajmadison.com', // 5,951
	'i.ytimg.com', // 3,726
	'pisces.bbystatic.com', // 2,612
	'encrypted-tbn0.gstatic.com', // 1,576
	'images.samsung.com', // 512
	'kitchenaid-h.assetsadobe.com', // 466
	'media.us.lg.com', // 437
	'frigidaire.bynder.com', // 324
	'cdn.shopify.com', // 247
	'image-us.samsung.com', // 217
	'premiumlevella.com', // 194
	'www.brandsmartusa.com', // 183
	'cdn11.bigcommerce.com', // 140
	'www.cuisinart.com', // 138
	'www.avantiproducts.com', // 136
	'mobileimages.lowes.com', // 108
	'images2.productserve.com', // 97
	'elementelectronics.com', // 71
	'www.lg.com', // 56
	'images.thdstatic.com', // 53
	'web-res.midea.com', // 46
	'cf-images.us-east-1.prod.boltdns.net', // 46
	'images.unsplash.com', // 39
	'www.cpsc.gov', // 30
	'www.midea.com', // 24
	'image-stgus.samsung.com', // 22
	'media3.bsh-group.com', // 22
	'static.wixstatic.com', // 20
	'drtr5fjqqz6ee.cloudfront.net', // 20
	'photos-us.bazaarvoice.com', // 20
	'images.webfronts.com', // 15
	'uniqueappliances.com', // 12
	'cdnweb.danby.com', // 10
	'multimedia.bbycastatic.ca', // 10
	// Long tail (≤9 uses each)
	// FRIDGE-510 batch-2 unlock (08/24/2026): official manufacturer hosts for the
	// 14 low-res heroes whose only defect was an off-list source — Hestan (10),
	// Perlick (3), Blaze (1). URLs verified real-size by byte-sniff before adding.
	'hestanappliances.com',
	'api.perlick.com',
	'www.blazegrills.com',
	'www.blackanddecker.com',
	'cool-ski.com',
	'www.wine.com',
	'koolmore.com',
	'i.ebayimg.com',
	'i8.amplience.net',
	'ironmountainrefrigeration.com',
	'vinturi.com',
	'd229whyy0854hb.cloudfront.net',
	'i5.walmartimages.com',
	'www.pur.com',
	'i5.samsclubimages.com',
	'storage.googleapis.com',
	'www.u-line.com',
	'crdms.images.consumerreports.org',
	'i.rtings.com',
	'vinotemp.com',
	'assets.costway.ca',
	'hamiltonbeach.com',
	'www.aeitto.com',
	'electroluxmx.vtexassets.com',
	'd2j6dbq0eux0bg.cloudfront.net',
	'cdn10.bigcommerce.com',
	'www.danby.com',
	'www.electricswarehouse.com',
	'www.ivationproducts.com',
	'calefort.com',
	'backyardprovider.com',
	'www.thdstatic.com',
	'www.turboairinc.com',
	'nutrichefkitchen.com',
	'middleby-cdn.com',
	'olaretaildeals.com',
	'owp.klarna.com',
	'media.copeland.com',
	'a.mtstatic.com',
	'plus.unsplash.com',
	'iprsoftwaremedia.com',
	// Press-release imagery (added 08/16/2026 by the live re-check).
	'img.einpresswire.com',
];

// Wildcard subdomain patterns carried over from the pre-existing entries.
// These are scoped to known retailer/brand/CDN apex domains — not the open
// '**' — so they stay after Step 2. Candidates for tightening with log data.
const IMAGE_HOST_PATTERNS = [
	'**.homedepot.com',
	'**.lowes.com',
	'**.bestbuy.com',
	'**.lg.com',
	'**.samsung.com',
	'**.whirlpool.com',
	'**.geappliances.com',
	'**.frigidaire.com',
	'**.kitchenaid.com',
	'**.bosch-home.com',
	'**.electrolux.com',
	'**.bigcommerce.com',
	'**.akamaized.net',
	'**.cloudfront.net',
	'**.brandsmartusa.com',
	'**.ajmadison.com',
	'**.assetsadobe.com',
];

if (supabaseHost) IMAGE_HOSTS.push(supabaseHost);

// FRIDGE-621 (08/29/2026) — ad-creative hosts, for the CSP img-src ONLY.
//
// Every display-ad creative in lib/ad-config.ts hotlinks an affiliate-network
// CDN, and none of those hosts were on the img-src allowlist after FRIDGE-139
// narrowed it from the blanket `https:` — so the browser blocked every ad
// image sitewide and each slot fell back to a placeholder box ("broken ads").
// Measured 08/29/2026: all 14 creative URLs return 200 image/* when fetched
// directly; the served CSP allowed zero of their hosts.
//
// CSP checks EVERY hop of a redirect chain, so the 302 redirectors AND their
// CDN targets are both listed (chains measured 08/29/2026, curl per hop).
//
// Deliberately NOT merged into IMAGE_HOSTS: that list also opens
// /_next/image (FRIDGE-156), and ad creatives render through plain <img>,
// never the optimizer. The guard test pins both directions:
// tests/ad-creative-csp-coverage.test.ts.
const AD_IMAGE_HOSTS = [
	// CJ — 302 redirectors used by wayfair / lg / ajmadison units…
	'www.tqlkg.com',
	'www.awltovhc.com',
	'www.lduhtrp.net',
	// …which all land on CJ's creative CDN.
	'www.yceml.net',
	// Impact — levot, sharpSquare, restaurant ×2, sharp ×2 (direct 200s).
	'a.impactradius-go.com',
	// Partnerize — fridgefilters (direct 200).
	'console.partnerize.com',
	// Pepperjam/Partnerize — freshmealplan: 302 redirector → CDN.
	'www.gopjn.com',
	'media.pepperjamnetwork.com',
	// Rakuten — amana, maytag: 302 redirector → banner CDN.
	'ad.linksynergy.com',
	'mproxy.banner.linksynergy.com',
	// FlexOffers shared images — the True Nature Meats unit (direct 200).
	'content.flexlinks.com',
];

// Next.js hard-caps images.remotePatterns at 50 entries (zod `.max(50)` in
// next/dist/server/config-schema.js — PR #622's first Vercel build failed on
// exactly this). One entry per host needs ~93 and does not fit, and folding
// hosts up to their apex does not help: the list spans ~65 distinct
// registrable domains, so `**.apex` saves almost nothing while widening what
// is allowed.
//
// So hosts are PACKED, several to an entry, using brace alternation. Next
// compiles a pattern's hostname with picomatch at build time and writes the
// resulting regex source into images-manifest.json
// (next/dist/build/index.js writeImagesManifest → `makeRe(p.hostname).source`),
// and that regex is what Vercel's own optimizer runs — its Build Output API
// documents hostname as an anchored regex (`^via\.placeholder\.com$`). So the
// braces never reach Vercel; only the compiled regex does.
//
// The packing is EXACT, not a widening: `{a.com,b.com}` compiles to
// `^(?:(a\.com|b\.com))$`, which rejects `x.a.com` and `a.com.evil.com` just
// as a literal entry would. Verified against the real matcher in
// tests/image-host-allowlist.test.ts.
const MAX_REMOTE_PATTERNS = 50;
// Entries the config spends outside the packed groups: the scoped subdomain
// wildcards, plus the single http://localhost entry for dev.
const RESERVED_ENTRIES = IMAGE_HOST_PATTERNS.length + 1;
// Readability floor. Groups stay ~20 hosts wide until the list grows big
// enough that it must pack tighter to stay inside the cap, so the budget
// holds by construction however long IMAGE_HOSTS gets.
const GROUP_SIZE_FLOOR = 20;

/**
 * Pack exact hostnames into brace-alternation patterns that fit the
 * remotePatterns cap. Returns pattern strings, not entries.
 */
function packImageHosts(hosts = IMAGE_HOSTS) {
	const groupBudget = MAX_REMOTE_PATTERNS - RESERVED_ENTRIES;
	const size = Math.max(GROUP_SIZE_FLOOR, Math.ceil(hosts.length / groupBudget));
	const groups = [];
	for (let i = 0; i < hosts.length; i += size) {
		groups.push('{' + hosts.slice(i, i + size).join(',') + '}');
	}
	return groups;
}

// The Supabase Storage buckets next.config.js proxies at /<bucket>/… (its
// rewrites and images.localPatterns). A path in one of them may carry a `?v=`
// cache-buster and next/image still accepts it (localPatterns: no `search` key
// on these). FRIDGE-1547: lib/image-optimizable.ts reads the same list, so its
// check accepts exactly what the config accepts.
const STORAGE_PROXY_BUCKETS = [
	'downloads',
	'brand-logos',
	'menu-images',
	'press-logos',
	'company-logos',
	'authors',
	'product-images',
	'blog-images',
	'state-flags',
];

module.exports = {
	IMAGE_HOSTS,
	IMAGE_HOST_PATTERNS,
	AD_IMAGE_HOSTS,
	packImageHosts,
	MAX_REMOTE_PATTERNS,
	STORAGE_PROXY_BUCKETS,
};
